<template>
    <li>
        <DsTooltip
            :offset="{ skidding: 0, distance: 16 }"
            :tooltipText
            tooltipLocation="right"
        >
            <component
                :is="navigation.is"
                :aria-label="!expanded ? (label ?? tooltipText) : undefined"
                class="ds-nav-item flex items-center rounded-lg p-3 text-content-inverse no-underline"
                :class="{
                    'bg-ds-cream-100 text-content-primary': isActive,
                    'w-full justify-center': !expanded,
                }"
                v-bind="navigation.attrs"
                @click="navigation.is === 'button' && $emit('click')"
            >
                <div
                    v-if="icon || firstName || label"
                    class="flex gap-2"
                >
                    <DsIcon
                        v-if="icon"
                        :aria-hidden="!!label || undefined"
                        :icon
                        size="md"
                    />
                    <DsAvatar.Initials
                        v-else-if="firstName"
                        :aria-hidden="!!label || undefined"
                        :firstName
                        size="sm"
                    />
                    <span
                        v-if="label"
                        v-show="expanded"
                        class="text-content-lg font-medium"
                    >
                        {{ label }}
                    </span>
                </div>

                <div class="flex flex-1 items-center justify-end">
                    <slot />
                </div>
            </component>
        </DsTooltip>
    </li>
</template>

<script setup lang="ts">
import DsAvatar from '@/components/design-system/avatar';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsTooltip from '@/components/design-system/tooltip/DsTooltip.vue';
import type { DsTooltipProps } from '@/components/design-system/tooltip/tooltip.types';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';
import { useRoute, type RouteLocationRaw } from 'vue-router';

const props = withDefaults(
    defineProps<{
        expanded?: boolean;
        firstName?: string;
        icon?: FontAwesomeIconProps['icon'];
        label?: string;
        to?: RouteLocationRaw;
        url?: HTMLAnchorElement['href'];
        tooltipText?: DsTooltipProps['tooltipText'];
    }>(),
    {
        expanded: true,
    },
);

defineEmits<{
    click: [];
}>();

const route = useRoute();

const isActive = computed(() => {
    if (!props.url) return false;
    return route.path === props.url || route.path.startsWith(`${props.url}/`);
});

const navigation = computed(() => {
    if (props.to) return { is: 'router-link', attrs: { to: props.to, activeClass: 'bg-ds-cream-100 text-content-primary' } };
    if (props.url) return { is: 'a', attrs: { href: props.url } };
    return { is: 'button', attrs: { type: 'button' } };
});
</script>

<style scoped lang="scss">
.ds-nav-item {
    @apply outline-none;

    &:hover {
        @apply bg-semantic-on-dark-hover;
    }

    &:active {
        @apply bg-semantic-on-dark-pressed;
    }

    &:focus-visible {
        @apply ring-4 ring-ds-primary-300;
    }
}
</style>
